import { useState } from 'react';
import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ConfirmDialog } from './ConfirmDialog';
import {
  getPmIncidentDetail,
  addIncidentNote,
  closeIncident,
  reopenIncident,
  assignIncident,
  getPmUsers,
} from '@/services/api.client';
import type { IncidentStatus, IncidentSummary } from '@/types';

const STATUS_VARIANTS: Record<IncidentStatus, 'default' | 'secondary' | 'destructive' | 'outline'> =
  {
    open: 'destructive',
    under_review: 'secondary',
    closed: 'outline',
  };

interface IncidentDetailPanelProps {
  incident: IncidentSummary | null;
  propertyId: string;
  onClose: () => void;
}

export function IncidentDetailPanel({ incident, propertyId, onClose }: IncidentDetailPanelProps) {
  const qc = useQueryClient();
  const [note, setNote] = useState('');
  const [closeDialog, setCloseDialog] = useState(false);
  const [reopenDialog, setReopenDialog] = useState(false);

  const detailQuery = useQuery({
    queryKey: ['pm', 'incident', propertyId, incident?.id],
    queryFn: () => getPmIncidentDetail(propertyId, incident?.id ?? ''),
    enabled: !!incident,
  });

  const noteMutation = useMutation({
    mutationFn: () => addIncidentNote(propertyId, incident?.id ?? '', note),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pm', 'incident', propertyId, incident?.id ?? ''] });
      setNote('');
    },
    onError: () => {
      toast.error('Failed to save note. Please try again.');
    },
  });

  const closeMutation = useMutation({
    mutationFn: (resolutionNote: string) =>
      closeIncident(propertyId, incident?.id ?? '', resolutionNote),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pm', 'incidents', propertyId] });
      void qc.invalidateQueries({ queryKey: ['pm', 'incident', propertyId, incident?.id ?? ''] });
      setCloseDialog(false);
    },
  });

  const reopenMutation = useMutation({
    mutationFn: (reason: string) => reopenIncident(propertyId, incident?.id ?? '', reason),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pm', 'incidents', propertyId] });
      void qc.invalidateQueries({ queryKey: ['pm', 'incident', propertyId, incident?.id ?? ''] });
      setReopenDialog(false);
    },
  });

  const supervisorsQuery = useQuery({
    queryKey: ['pm', 'users', propertyId, 'security_supervisor'],
    queryFn: () => getPmUsers(propertyId, { role: 'security_supervisor' }),
    enabled: !!incident && (incident.status === 'open' || incident.status === 'under_review'),
  });

  const assignMutation = useMutation({
    mutationFn: (supervisorId: string) =>
      assignIncident(propertyId, incident?.id ?? '', supervisorId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pm', 'incidents', propertyId] });
      void qc.invalidateQueries({ queryKey: ['pm', 'incident', propertyId, incident?.id ?? ''] });
      toast.success('Incident assigned');
    },
    onError: () => {
      toast.error('Failed to assign incident. Please try again.');
    },
  });

  if (!incident) return null;
  const detail = detailQuery.data;

  return (
    <>
      <Sheet
        open
        onOpenChange={(o) => {
          if (!o) onClose();
        }}
      >
        <SheetContent showCloseButton={false} className="w-[420px] sm:w-[420px] overflow-y-auto">
          <SheetHeader className="flex flex-row items-center justify-between mb-4">
            <SheetTitle className="text-base">{incident.referenceNumber}</SheetTitle>
            <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close">
              <X className="h-4 w-4" />
            </Button>
          </SheetHeader>

          {detailQuery.isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-6 w-full" />
              ))}
            </div>
          ) : detail ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Badge variant={STATUS_VARIANTS[detail.status]}>
                  {detail.status.replace('_', ' ')}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {new Date(detail.loggedAt).toLocaleString()}
                </span>
              </div>

              <div className="rounded-md bg-muted/50 p-3 space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Type</span>
                  <span className="font-medium">{detail.type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Gate</span>
                  <span className="font-medium">{detail.gateName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Guard</span>
                  <span className="font-medium">{detail.guardName}</span>
                </div>
                {detail.assignedToName && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Assigned to</span>
                    <span className="font-medium">{detail.assignedToName}</span>
                  </div>
                )}
              </div>

              <div className="rounded-md bg-muted/50 p-3">
                <p className="text-xs font-medium text-muted-foreground uppercase mb-1">
                  Description
                </p>
                <p className="text-sm">{detail.description}</p>
              </div>

              {detail.notes.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase mb-2">
                    Internal Notes
                  </p>
                  <ol className="space-y-2">
                    {detail.notes.map((n) => (
                      <li key={n.id} className="rounded-md bg-muted/50 p-3 text-sm">
                        <p className="font-medium">{n.authorName}</p>
                        <p className="text-muted-foreground text-xs mb-1">
                          {new Date(n.createdAt).toLocaleString()}
                        </p>
                        <p>{n.body}</p>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              <div className="space-y-2">
                <Textarea
                  placeholder="Add internal note…"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                />
                <Button
                  size="sm"
                  variant="outline"
                  disabled={note.trim().length === 0 || noteMutation.isPending}
                  onClick={() => noteMutation.mutate()}
                >
                  {noteMutation.isPending ? 'Saving…' : 'Add Note'}
                </Button>
              </div>

              <div className="flex gap-2 pt-2 border-t">
                {detail.status !== 'closed' && (supervisorsQuery.data?.data ?? []).length > 0 && (
                  <div className="w-full space-y-1 mb-2">
                    <label className="text-xs font-medium text-muted-foreground">
                      Assign to Supervisor
                    </label>
                    <Select
                      onValueChange={(v) => assignMutation.mutate(v)}
                      disabled={assignMutation.isPending}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select supervisor…" />
                      </SelectTrigger>
                      <SelectContent>
                        {(supervisorsQuery.data?.data ?? []).map((sup) => (
                          <SelectItem key={sup.id} value={sup.id}>
                            {sup.fullName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {detail.status !== 'closed' ? (
                  <Button size="sm" variant="destructive" onClick={() => setCloseDialog(true)}>
                    Close Incident
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => setReopenDialog(true)}>
                    Re-open
                  </Button>
                )}
              </div>

              {detail.resolutionNote && (
                <div className="rounded-md bg-muted/50 p-3">
                  <p className="text-xs font-medium text-muted-foreground uppercase mb-1">
                    Resolution
                  </p>
                  <p className="text-sm">{detail.resolutionNote}</p>
                </div>
              )}
            </div>
          ) : null}
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={closeDialog}
        title="Close Incident"
        description="Provide a resolution note. This is required and audit-logged."
        confirmLabel="Close Incident"
        requireReason
        reasonLabel="Resolution note"
        minReasonLength={20}
        loading={closeMutation.isPending}
        onConfirm={(reason) => closeMutation.mutate(reason ?? '')}
        onCancel={() => setCloseDialog(false)}
      />

      <ConfirmDialog
        open={reopenDialog}
        title="Re-open Incident"
        description="Provide a reason for re-opening this incident."
        confirmLabel="Re-open"
        requireReason
        reasonLabel="Reason"
        minReasonLength={10}
        loading={reopenMutation.isPending}
        onConfirm={(reason) => reopenMutation.mutate(reason ?? '')}
        onCancel={() => setReopenDialog(false)}
      />
    </>
  );
}
